import React, { useState, useEffect } from 'react'
import {
  Card, List, Input, Select, DatePicker, Button, Space, Tag, Avatar, Typography, message, Collapse, Statistic,
  Checkbox, Switch, InputNumber, Modal, Drawer, Tooltip
} from 'antd'
import {
  SearchOutlined, MessageOutlined, UserOutlined, ClockCircleOutlined, DollarOutlined,
  DesktopOutlined, MobileOutlined, CodeOutlined, GlobalOutlined, QuestionCircleOutlined,
  PictureOutlined, NodeIndexOutlined, DeleteOutlined, EyeOutlined, TagOutlined,
  ExportOutlined, FilterOutlined, ReloadOutlined
} from '@ant-design/icons'
import { conversationApi } from '@/services/api'
import MessageContent from '../../components/MessageContent'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker
const { Option } = Select
const { Text, Paragraph, Title } = Typography

interface ChatGroup {
  user_id: string
  conversation_count: number
  message_count: number
  total_tokens: number | string  // 🔥 修复：支持字符串类型
  total_cost: number | string    // 🔥 修复：支持字符串类型
  input_cost: number | string    // 🔥 修复：支持字符串类型
  output_cost: number | string   // 🔥 修复：支持字符串类型
  last_activity: number
  conversations: Conversation[]
  // 🔥 新增字段
  canvas_nodes_count: number
  canvas_sessions_count: number
  chat_sessions_count: number
  client_types_list: string[]
  models_used_list: string[]
  real_canvas_sessions: number
  random_canvas_sessions: number
  sessions_with_images: number
  image_messages_count: number
  canvas_adoption_rate: string
  images_usage_rate: string
  primary_context_mode: 'mixed' | 'chat-only'
}

interface Conversation {
  id: number
  user_id: string
  chat_id: string
  model: string
  title: string
  message_count: number
  last_message: string
  total_tokens: number
  created_at: number
  updated_at: number
  billing?: {
    total_cost: number
    input_cost: number
    output_cost: number
    currency: string
  }
  // 🔥 新增字段
  canvas_nodes_count: number
  latest_result_id: string
  canvas_type: 'real' | 'random'
  canvas_id: string
  context_mode: 'canvas-based-real' | 'canvas-based-random' | 'chat-based'
  canvas_mode_display: string
  context_identifier: string
  is_canvas_session: boolean
  client_type?: string
  client_version?: string
  session_tags?: string[]
  is_archived?: boolean
  has_images?: boolean
}

const ChatManagementPage: React.FC = () => {
  const [chatGroups, setChatGroups] = useState<ChatGroup[]>([])
  const [loading, setLoading] = useState(false)
  const [, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })

  // 🔥 增强的筛选条件
  const [filters, setFilters] = useState({
    userId: '',
    model: '',
    keyword: '',
    dateRange: null as [dayjs.Dayjs, dayjs.Dayjs] | null,
    // 新增筛选条件
    contextMode: 'all' as 'all' | 'canvas-based' | 'chat-based',
    clientType: 'all',
    canvasType: 'all' as 'all' | 'real' | 'random',
    hasImages: undefined as boolean | undefined,
    minTokens: undefined as number | undefined,
    maxTokens: undefined as number | undefined,
    minCost: undefined as number | undefined,
    maxCost: undefined as number | undefined,
    isArchived: false
  })

  // 🔥 批量操作状态
  const [selectedConversations, setSelectedConversations] = useState<number[]>([])
  const [batchModalVisible, setBatchModalVisible] = useState(false)
  const [batchAction, setBatchAction] = useState<'delete' | 'archive' | 'tag' | 'export'>('delete')

  // 🔥 高级筛选面板状态
  const [advancedFiltersVisible, setAdvancedFiltersVisible] = useState(false)

  // 🔥 会话详情相关状态
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [conversationDetailVisible, setConversationDetailVisible] = useState(false)
  const [messages, setMessages] = useState<any[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)

  // 🔥 增强的数据获取方法
  const fetchChatGroups = async (page = 1) => {
    try {
      setLoading(true)

      // 🔥 构建新的查询参数
      const params = {
        userId: filters.userId || undefined,
        model: filters.model || undefined,
        keyword: filters.keyword || undefined,
        dateRange: filters.dateRange ? [
          filters.dateRange[0].valueOf(),
          filters.dateRange[1].valueOf()
        ] as [number, number] : undefined,
        contextMode: filters.contextMode,
        clientType: filters.clientType !== 'all' ? filters.clientType : undefined,
        canvasType: filters.canvasType !== 'all' ? (filters.canvasType === 'random' ? 'virtual' as const : filters.canvasType as 'real' | 'virtual') : undefined, // 🔥 修复：映射random到virtual
        hasImages: filters.hasImages,
        minTokens: filters.minTokens,
        maxTokens: filters.maxTokens,
        minCost: filters.minCost,
        maxCost: filters.maxCost,
        isArchived: filters.isArchived
      }

      // 🔥 调用新的增强 API
      const response = await conversationApi.getChatGroups(params)
      if (response.success) {
        // 🔥 修复：确保response.data是数组类型
        const chatGroupsData = Array.isArray(response.data) ? response.data : []
        setChatGroups(chatGroupsData)
        setPagination(prev => ({
          ...prev,
          current: page,
          total: chatGroupsData.length || 0
        }))
      } else {
        message.error('获取聊天数据失败')
        setChatGroups([])
      }
    } catch (error) {
      console.error('获取聊天数据失败:', error)
      message.error('获取聊天数据失败')
      setChatGroups([])
    } finally {
      setLoading(false)
    }
  }

  // 🔥 客户端图标映射
  const getClientIcon = (clientType: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      nextchat: <DesktopOutlined style={{ color: '#1890ff' }} />,
      chatbox: <MobileOutlined style={{ color: '#52c41a' }} />,
      curl: <CodeOutlined style={{ color: '#722ed1' }} />,
      python: <CodeOutlined style={{ color: '#fa8c16' }} />,
      nodejs: <CodeOutlined style={{ color: '#13c2c2' }} />,
      browser: <GlobalOutlined style={{ color: '#eb2f96' }} />,
      unknown: <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
    }
    return iconMap[clientType] || iconMap.unknown
  }

  // 🔥 获取客户端显示名称
  const getClientDisplayName = (clientType: string) => {
    const nameMap: { [key: string]: string } = {
      nextchat: 'NextChat',
      chatbox: 'ChatBox',
      curl: 'cURL',
      python: 'Python',
      nodejs: 'Node.js',
      browser: '浏览器',
      unknown: '未知'
    }
    return nameMap[clientType] || clientType
  }

  useEffect(() => {
    fetchChatGroups()
  }, [filters])

  const formatCost = (cost: any) => {
    // 🔥 修复：确保输入是数字类型
    const numCost = Number(cost);
    if (isNaN(numCost) || cost === null || cost === undefined) {
      return '0.0000'
    }
    return numCost < 0.01 ? numCost.toFixed(6) : numCost.toFixed(4)
  }

  // 🔥 处理查看会话详情
  const handleViewConversationDetail = async (conversation: Conversation) => {
    setSelectedConversation(conversation)
    setConversationDetailVisible(true)
    setMessagesLoading(true)

    try {
      // 🔥 修复：使用正确的API函数获取会话详情和消息
      const response = await conversationApi.getConversationDetail(conversation.user_id, conversation.chat_id)
      if (response.success && response.data) {
        // 设置消息数据
        setMessages(response.data.messages || [])

        // 🔥 修复：更新会话信息，包含计费数据
        const updatedConversation = {
          ...conversation,
          ...response.data.conversation,
          billing: response.data.billing || conversation.billing
        }
        setSelectedConversation(updatedConversation)
      } else {
        message.error('获取会话详情失败')
        setMessages([])
      }
    } catch (error) {
      console.error('获取会话详情失败:', error)
      message.error('获取会话详情失败')
      setMessages([])
    } finally {
      setMessagesLoading(false)
    }
  }

  // 🔥 增强的会话项渲染
  const renderConversationItem = (conversation: Conversation) => (
    <List.Item
      key={conversation.id}
      actions={[
        <Button
          key="detail"
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewConversationDetail(conversation)}
        >
          详情
        </Button>,
        <Checkbox
          key="select"
          checked={selectedConversations.includes(conversation.id)}
          onChange={e => {
            if (e.target.checked) {
              setSelectedConversations(prev => [...prev, conversation.id]);
            } else {
              setSelectedConversations(prev => prev.filter(id => id !== conversation.id));
            }
          }}
        />
      ]}
    >
      <List.Item.Meta
        avatar={<Avatar icon={<MessageOutlined />} />}
        title={
          <Space wrap>
            <Text strong>{conversation.title}</Text>
            <Tag color="blue">{conversation.model}</Tag>

            {/* 🔥 画布模式标签 */}
            {conversation.is_canvas_session && (
              <Tag
                color={conversation.canvas_type === 'real' ? 'gold' : 'purple'}
                icon={<NodeIndexOutlined />}
              >
                {conversation.canvas_mode_display}
              </Tag>
            )}

            {/* 🔥 客户端类型标签 */}
            {conversation.client_type && conversation.client_type !== 'unknown' && (
              <Tag icon={getClientIcon(conversation.client_type)}>
                {getClientDisplayName(conversation.client_type)}
                {conversation.client_version && ` v${conversation.client_version}`}
              </Tag>
            )}

            {/* 🔥 图片标签 */}
            {conversation.has_images && (
              <Tag color="orange" icon={<PictureOutlined />}>
                包含图片
              </Tag>
            )}

            {/* 🔥 费用标签 */}
            {conversation.billing && (
              <Tag icon={<DollarOutlined />} color="green">
                ${formatCost(conversation.billing.total_cost)}
              </Tag>
            )}
          </Space>
        }
        description={
          <div>
            <Paragraph ellipsis={{ rows: 2 }} style={{ margin: 0, marginBottom: 8 }}>
              {conversation.last_message}
            </Paragraph>
            <Space size="large" wrap>
              <Text type="secondary">
                <MessageOutlined /> {conversation.message_count} 条消息
              </Text>
              <Text type="secondary">
                {conversation.total_tokens?.toLocaleString() || 0} tokens
              </Text>
              {/* 🔥 Canvas节点数 */}
              {conversation.canvas_nodes_count > 0 && (
                <Text type="secondary">
                  <NodeIndexOutlined /> {conversation.canvas_nodes_count} 个节点
                </Text>
              )}
              <Text type="secondary">
                <ClockCircleOutlined /> {dayjs(conversation.created_at).format('MM-DD HH:mm')}
              </Text>
            </Space>
          </div>
        }
      />
    </List.Item>
  )

  // 🔥 增强的聊天组渲染
  const renderChatGroup = (group: ChatGroup) => {
    const collapseItems = [
      {
        key: group.user_id,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Space>
              <Avatar icon={<UserOutlined />} />
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  用户: {group.user_id.substring(0, 12)}...
                </Title>
                <Space wrap style={{ marginTop: 4 }}>
                  <Text type="secondary">
                    最后活动: {dayjs(group.last_activity).format('YYYY-MM-DD HH:mm')}
                  </Text>

                  {/* 🔥 上下文模式标签 */}
                  <Tag color={group.primary_context_mode === 'mixed' ? 'blue' : 'default'}>
                    {group.primary_context_mode === 'mixed' ? '混合模式' : '纯聊天'}
                  </Tag>

                  {/* 🔥 客户端类型标签 */}
                  {group.client_types_list && group.client_types_list.length > 0 && (
                    <Space size={4}>
                      {group.client_types_list.slice(0, 3).map(clientType => (
                        <Tag key={clientType} icon={getClientIcon(clientType)} style={{ fontSize: '12px' }}>
                          {getClientDisplayName(clientType)}
                        </Tag>
                      ))}
                      {group.client_types_list.length > 3 && (
                        <Tag style={{ fontSize: '12px' }}>+{group.client_types_list.length - 3}</Tag>
                      )}
                    </Space>
                  )}
                </Space>
              </div>
            </Space>

            {/* 🔥 增强的统计信息 */}
            <Space size="large">
              <Statistic
                title="会话数"
                value={group.conversation_count}
                valueStyle={{ fontSize: 16 }}
              />
              <Statistic
                title="消息数"
                value={group.message_count}
                valueStyle={{ fontSize: 16 }}
              />
              <Statistic
                title="Token数"
                value={Number(group.total_tokens) || 0}
                valueStyle={{ fontSize: 16 }}
              />
              {/* 🔥 Canvas统计 */}
              {group.canvas_nodes_count > 0 && (
                <Statistic
                  title="Canvas节点"
                  value={group.canvas_nodes_count}
                  valueStyle={{ fontSize: 16, color: '#722ed1' }}
                />
              )}
              {/* 🔥 图片统计 */}
              {group.sessions_with_images > 0 && (
                <Statistic
                  title="含图片会话"
                  value={group.sessions_with_images}
                  valueStyle={{ fontSize: 16, color: '#fa8c16' }}
                />
              )}
              <Statistic
                title="总费用"
                value={`$${formatCost(group.total_cost)}`}
                valueStyle={{ fontSize: 16, color: '#52c41a' }}
              />
            </Space>
          </div>
        ),
        children: (
          <div>
            {/* 🔥 详细统计信息 */}
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
              <Space size="large" wrap>
                <Statistic
                  title="Canvas采用率"
                  value={`${group.canvas_adoption_rate}%`}
                  valueStyle={{ fontSize: 14 }}
                />
                <Statistic
                  title="图片使用率"
                  value={`${group.images_usage_rate}%`}
                  valueStyle={{ fontSize: 14 }}
                />
                <Statistic
                  title="真实画布会话"
                  value={group.real_canvas_sessions}
                  valueStyle={{ fontSize: 14 }}
                />
                <Statistic
                  title="随机画布会话"
                  value={group.random_canvas_sessions}
                  valueStyle={{ fontSize: 14 }}
                />
                <Statistic
                  title="使用模型数"
                  value={group.models_used_list?.length || 0}
                  valueStyle={{ fontSize: 14 }}
                />
              </Space>

              {/* 🔥 使用的模型列表 */}
              {group.models_used_list && group.models_used_list.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>使用的模型: </Text>
                  <Space size={4} wrap>
                    {group.models_used_list.map(model => (
                      <Tag key={model} color="blue" style={{ fontSize: '12px' }}>{model}</Tag>
                    ))}
                  </Space>
                </div>
              )}
            </Card>

            {/* 🔥 会话列表 */}
            <List
              dataSource={group.conversations}
              renderItem={renderConversationItem}
              pagination={{
                pageSize: 5,
                size: 'small',
                showSizeChanger: false
              }}
            />
          </div>
        )
      }
    ];

    return (
      <Card key={group.user_id} style={{ marginBottom: 16 }}>
        <Collapse items={collapseItems} />
      </Card>
    )
  }

  return (
    <div style={{ padding: '0' }}>
      <Card title="聊天管理" extra={
        <Space>
          {/* 🔥 批量操作按钮 */}
          {selectedConversations.length > 0 && (
            <>
              <Button
                icon={<DeleteOutlined />}
                onClick={() => {
                  setBatchAction('delete');
                  setBatchModalVisible(true);
                }}
                danger
              >
                批量删除 ({selectedConversations.length})
              </Button>
              <Button
                icon={<ExportOutlined />}
                onClick={() => {
                  setBatchAction('export');
                  setBatchModalVisible(true);
                }}
              >
                批量导出 ({selectedConversations.length})
              </Button>
            </>
          )}
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => fetchChatGroups()}
          >
            刷新数据
          </Button>
        </Space>
      }>
        {/* 🔥 基础筛选条件 */}
        <Space wrap style={{ marginBottom: '16px' }}>
          <Input
            placeholder="用户ID"
            value={filters.userId}
            onChange={e => setFilters(prev => ({ ...prev, userId: e.target.value }))}
            style={{ width: 150 }}
          />
          <Select
            placeholder="选择模型"
            value={filters.model || undefined}
            onChange={value => setFilters(prev => ({ ...prev, model: value || '' }))}
            style={{ width: 200 }}
            allowClear
          >
            <Option value="anthropic/claude-3.7-sonnet">Claude 3.7 Sonnet</Option>
            <Option value="anthropic/claude-3.5-haiku">Claude 3.5 Haiku</Option>
            <Option value="anthropic/claude-sonnet-4">Claude Sonnet 4</Option>
            <Option value="openai/gpt-4.1">GPT-4.1</Option>
            <Option value="openai/gpt-4o">GPT-4o</Option>
            <Option value="deepseek/deepseek-r1">DeepSeek R1</Option>
          </Select>
          <Input
            placeholder="搜索关键词"
            value={filters.keyword}
            onChange={e => setFilters(prev => ({ ...prev, keyword: e.target.value }))}
            style={{ width: 200 }}
            prefix={<SearchOutlined />}
          />
          <RangePicker
            value={filters.dateRange}
            onChange={value => setFilters(prev => ({
              ...prev,
              dateRange: value as [dayjs.Dayjs, dayjs.Dayjs] | null
            }))}
            placeholder={['开始日期', '结束日期']}
          />

          {/* 🔥 高级筛选按钮 */}
          <Button
            icon={<FilterOutlined />}
            onClick={() => setAdvancedFiltersVisible(!advancedFiltersVisible)}
            type={advancedFiltersVisible ? 'primary' : 'default'}
          >
            高级筛选
          </Button>
        </Space>

        {/* 🔥 高级筛选面板 */}
        {advancedFiltersVisible && (
          <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
            <Space wrap>
              {/* 上下文模式 */}
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>上下文模式</Text>
                <Select
                  value={filters.contextMode}
                  onChange={value => setFilters(prev => ({ ...prev, contextMode: value }))}
                  style={{ width: 120 }}
                >
                  <Option value="all">全部</Option>
                  <Option value="canvas-based">Canvas模式</Option>
                  <Option value="chat-based">纯聊天</Option>
                </Select>
              </div>

              {/* 客户端类型 */}
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>客户端类型</Text>
                <Select
                  value={filters.clientType}
                  onChange={value => setFilters(prev => ({ ...prev, clientType: value }))}
                  style={{ width: 120 }}
                >
                  <Option value="all">全部</Option>
                  <Option value="nextchat">NextChat</Option>
                  <Option value="chatbox">ChatBox</Option>
                  <Option value="curl">cURL</Option>
                  <Option value="python">Python</Option>
                  <Option value="browser">浏览器</Option>
                </Select>
              </div>

              {/* 画布类型 */}
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>画布类型</Text>
                <Select
                  value={filters.canvasType}
                  onChange={value => setFilters(prev => ({ ...prev, canvasType: value }))}
                  style={{ width: 120 }}
                >
                  <Option value="all">全部</Option>
                  <Option value="real">真实画布</Option>
                  <Option value="random">随机画布</Option>
                </Select>
              </div>

              {/* 图片内容 */}
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>图片内容</Text>
                <Select
                  value={filters.hasImages === undefined ? 'all' : filters.hasImages ? 'yes' : 'no'}
                  onChange={value => setFilters(prev => ({
                    ...prev,
                    hasImages: value === 'all' ? undefined : value === 'yes'
                  }))}
                  style={{ width: 120 }}
                >
                  <Option value="all">全部</Option>
                  <Option value="yes">包含图片</Option>
                  <Option value="no">无图片</Option>
                </Select>
              </div>

              {/* Token范围 */}
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Token范围</Text>
                <Space.Compact>
                  <InputNumber
                    placeholder="最小"
                    value={filters.minTokens}
                    onChange={value => setFilters(prev => ({ ...prev, minTokens: value || undefined }))}
                    style={{ width: 80 }}
                    min={0}
                  />
                  <InputNumber
                    placeholder="最大"
                    value={filters.maxTokens}
                    onChange={value => setFilters(prev => ({ ...prev, maxTokens: value || undefined }))}
                    style={{ width: 80 }}
                    min={0}
                  />
                </Space.Compact>
              </div>

              {/* 费用范围 */}
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>费用范围($)</Text>
                <Space.Compact>
                  <InputNumber
                    placeholder="最小"
                    value={filters.minCost}
                    onChange={value => setFilters(prev => ({ ...prev, minCost: value || undefined }))}
                    style={{ width: 80 }}
                    min={0}
                    step={0.01}
                  />
                  <InputNumber
                    placeholder="最大"
                    value={filters.maxCost}
                    onChange={value => setFilters(prev => ({ ...prev, maxCost: value || undefined }))}
                    style={{ width: 80 }}
                    min={0}
                    step={0.01}
                  />
                </Space.Compact>
              </div>

              {/* 归档状态 */}
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>归档状态</Text>
                <Switch
                  checked={filters.isArchived}
                  onChange={value => setFilters(prev => ({ ...prev, isArchived: value }))}
                  checkedChildren="已归档"
                  unCheckedChildren="未归档"
                />
              </div>

              {/* 重置按钮 */}
              <div style={{ alignSelf: 'flex-end' }}>
                <Button
                  onClick={() => setFilters({
                    userId: '',
                    model: '',
                    keyword: '',
                    dateRange: null,
                    contextMode: 'all',
                    clientType: 'all',
                    canvasType: 'all',
                    hasImages: undefined,
                    minTokens: undefined,
                    maxTokens: undefined,
                    minCost: undefined,
                    maxCost: undefined,
                    isArchived: false
                  })}
                >
                  重置筛选
                </Button>
              </div>
            </Space>
          </Card>
        )}

        {/* 统计信息 */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space size="large">
            <Statistic
              title="总用户数"
              value={chatGroups.length}
              prefix={<UserOutlined />}
            />
            <Statistic
              title="总会话数"
              value={Array.isArray(chatGroups) ? chatGroups.reduce((sum, group) => sum + (group.conversation_count || 0), 0) : 0}
              prefix={<MessageOutlined />}
            />
            <Statistic
              title="总消息数"
              value={Array.isArray(chatGroups) ? chatGroups.reduce((sum, group) => sum + (group.message_count || 0), 0) : 0}
            />
            <Statistic
              title="总Token数"
              value={Array.isArray(chatGroups) ? chatGroups.reduce((sum, group) => sum + (Number(group.total_tokens) || 0), 0) : 0}
            />
            <Statistic
              title="总费用"
              value={`$${formatCost(Array.isArray(chatGroups) ? chatGroups.reduce((sum, group) => sum + (Number(group.total_cost) || 0), 0) : 0)}`}
              valueStyle={{ color: '#52c41a' }}
              prefix={<DollarOutlined />}
            />
          </Space>
        </Card>

        {/* 聊天组列表 */}
        <div style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
          {loading ? (
            <Card loading />
          ) : (
            chatGroups.map(renderChatGroup)
          )}
        </div>
      </Card>

      {/* 🔥 批量操作模态框 */}
      <Modal
        title={`批量${batchAction === 'delete' ? '删除' : '导出'}操作`}
        open={batchModalVisible}
        onCancel={() => setBatchModalVisible(false)}
        onOk={handleBatchOperation}
        okText="确认"
        cancelText="取消"
        okButtonProps={{
          danger: batchAction === 'delete',
          loading: loading
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>
            您确定要{batchAction === 'delete' ? '删除' : '导出'}
            <Text strong> {selectedConversations.length} </Text>
            个会话吗？
          </Text>
        </div>

        {batchAction === 'delete' && (
          <div style={{ padding: 12, backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 6 }}>
            <Text type="danger">
              ⚠️ 删除操作不可恢复，请谨慎操作！
            </Text>
          </div>
        )}

        {batchAction === 'export' && (
          <div>
            <Text type="secondary">
              将导出选中会话的详细信息，包括消息内容、统计数据等。
            </Text>
          </div>
        )}
      </Modal>

      {/* 🔥 会话详情抽屉 */}
      <Drawer
        title={
          <Space>
            <MessageOutlined />
            <span>会话详情</span>
            {selectedConversation && (
              <Tag color="blue">{selectedConversation.model}</Tag>
            )}
          </Space>
        }
        width={800}
        open={conversationDetailVisible}
        onClose={() => {
          setConversationDetailVisible(false)
          setSelectedConversation(null)
          setMessages([])
        }}
        extra={
          selectedConversation && (
            <Space>
              <Tooltip title="Token数量">
                <Tag icon={<TagOutlined />}>
                  {selectedConversation.total_tokens?.toLocaleString() || 0} tokens
                </Tag>
              </Tooltip>
              {selectedConversation.billing && (
                <Tooltip title="会话费用">
                  <Tag icon={<DollarOutlined />} color="green">
                    ${formatCost(selectedConversation.billing.total_cost)}
                  </Tag>
                </Tooltip>
              )}
            </Space>
          )
        }
      >
        {selectedConversation && (
          <div>
            {/* 会话基本信息 */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div><strong>会话ID:</strong> {selectedConversation.id}</div>
                <div><strong>用户ID:</strong> {selectedConversation.user_id}</div>
                <div><strong>聊天ID:</strong> {selectedConversation.chat_id}</div>
                <div><strong>模型:</strong> {selectedConversation.model}</div>
                <div><strong>标题:</strong> {selectedConversation.title}</div>
                <div><strong>消息数:</strong> {selectedConversation.message_count}</div>

                {/* 🔥 修复：Token数以小图标方式显示 */}
                <div>
                  <strong>Token数:</strong>
                  <Tooltip title={`Token数: ${selectedConversation.total_tokens?.toLocaleString() || 0}`}>
                    <Tag icon={<TagOutlined />} color="blue" style={{ marginLeft: 8 }}>
                      {selectedConversation.total_tokens?.toLocaleString() || 0} tokens
                    </Tag>
                  </Tooltip>
                </div>

                {/* 🔥 修复：费用以小图标方式显示 */}
                {selectedConversation.billing && selectedConversation.billing.total_cost > 0 && (
                  <div>
                    <strong>总费用:</strong>
                    <Tooltip title={`费用: $${formatCost(selectedConversation.billing.total_cost)} ${selectedConversation.billing.currency || 'USD'}`}>
                      <Tag
                        icon={<DollarOutlined />}
                        color="green"
                        style={{ marginLeft: 8 }}
                      >
                        ${formatCost(selectedConversation.billing.total_cost)} {selectedConversation.billing.currency || 'USD'}
                      </Tag>
                    </Tooltip>
                  </div>
                )}

                <div><strong>创建时间:</strong> {dayjs(selectedConversation.created_at).format('YYYY-MM-DD HH:mm:ss')}</div>
              </Space>
            </Card>

            {/* 消息列表 */}
            <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
              {messagesLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  加载中...
                </div>
              ) : messages.length > 0 ? (
                messages.map((message) => (
                  <Card
                    key={message.id}
                    size="small"
                    style={{ marginBottom: '8px' }}
                    title={
                      <Space>
                        <Tag color={message.role === 'user' ? 'green' : 'blue'}>
                          {message.role === 'user' ? '用户' : 'AI助手'}
                        </Tag>
                        <Text type="secondary">
                          {dayjs(message.created_at).format('HH:mm:ss')}
                        </Text>
                        {message.tokens > 0 && (
                          <Tooltip title="Token数量">
                            <Tag icon={<TagOutlined />}>
                              {message.tokens} tokens
                            </Tag>
                          </Tooltip>
                        )}
                        {message.billing && (
                          <Tooltip title="消息费用">
                            <Tag icon={<DollarOutlined />} color="green">
                              ${formatCost(message.billing.total_cost)}
                            </Tag>
                          </Tooltip>
                        )}
                      </Space>
                    }
                  >
                    <MessageContent
                      content={message.parsed_content || message.content}
                      role={message.role}
                      isCanvasNode={message.is_canvas_node}
                      resultId={message.result_id}
                    />
                  </Card>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  暂无消息数据
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  )

  // 🔥 批量操作处理函数
  async function handleBatchOperation() {
    if (selectedConversations.length === 0) {
      message.warning('请先选择要操作的会话');
      return;
    }

    try {
      setLoading(true);

      const response = await conversationApi.batchOperations(
        batchAction,
        selectedConversations,
        batchAction === 'export' ? { format: 'json' } : {}
      );

      if (response.success) {
        message.success(`批量${batchAction === 'delete' ? '删除' : '导出'}成功`);

        if (batchAction === 'delete') {
          // 刷新数据
          await fetchChatGroups();
        } else if (batchAction === 'export') {
          // 处理导出数据
          const blob = new Blob([JSON.stringify(response.data, null, 2)], {
            type: 'application/json'
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `conversations-export-${Date.now()}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }

        // 清空选择
        setSelectedConversations([]);
        setBatchModalVisible(false);
      } else {
        message.error(response.message || '操作失败');
      }
    } catch (error) {
      console.error('批量操作失败:', error);
      message.error('操作失败，请重试');
    } finally {
      setLoading(false);
    }
  }
}

export default ChatManagementPage
